import React, { useEffect, useState, useRef } from "react";
import styles from "./AddCategoriesPanel.module.css";
import {
  FaInfoCircle,
  FaRupeeSign,
  FaImage,
  FaFileAlt,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import WebService from "../../Services/WebService";
import { mediaUrl } from "../../Services/mediaUrl";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../Services/Store";
import CrossButton from "../../Customs/Button/CrossButton";
import Swal from "sweetalert2";
import {
  createWoodQuality,
  deleteWoodQuality,
  fetchWoodQualities,
  promptAddWoodQuality,
  promptWoodQualityName,
  updateWoodQuality,
} from "../../Services/woodQualities";

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  editCategory?: any;
}
interface DropdownOption {
  id: number;
  name: string;
}
const allowedImageTypes = ["image/jpeg", "image/png", "image/webp"];
const initialForm = {
  category_name: "",
  parent_category_id: "",
  basic_door_price: "",
  double_door_price: "",
  back_design_price: "",
  back_puc_price: "",
  sagwan_door_price: "",
  hsn_code: "",
  description: "",
  image: null as File | null,
};
const requiredFields = [
  "category_name",
  "basic_door_price",
  "double_door_price",
  "back_design_price",
  "back_puc_price",
  "sagwan_door_price",
];

const pricesFromCategory = (category: any): { [id: string]: string } => {
  const prices: { [id: string]: string } = {};
  if (!category) return prices;
  if (Array.isArray(category.qualities)) {
    category.qualities.forEach((q: any) => {
      const id = q.quality_id ?? q.id;
      if (id == null || q.price == null || q.price === "") return;
      prices[String(id)] = String(q.price);
    });
  }
  return prices;
};

const AddCategoriesPanel: React.FC<Props> = ({
  onClose,
  onSuccess,
  editCategory,
}) => {
  const [form, setForm] = useState<any>(initialForm);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [, setUnitList] = useState<DropdownOption[]>([]);
  const [catList, setCatList] = useState<DropdownOption[]>([]);
  const [qualityList, setQualityList] = useState<DropdownOption[]>([]);
  const [qualityPrices, setQualityPrices] = useState<{ [id: string]: string }>({});
  const [imgPreview, setImgPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- RBAC: Get inventory_management access
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const inventoryAccess = accessList.find((item: any) => item.code === "inventory_management");

  // Utility: Only allow numbers & decimals (no alpha, no negative)
  const isNumeric = (v: string) => v === "" || /^[0-9]+(\.[0-9]+)?$/.test(v);

  // Numeric fields
  const numericFields = [
    "basic_door_price",
    "double_door_price",
    "back_design_price",
    "back_puc_price",
    "sagwan_door_price",
  ];

  // Validation (matches Raw Material panel style)
  const validate = () => {
    const newErrors: { [k: string]: string } = {};
    // Required fields
    requiredFields.forEach(field => {
      const val = form[field];
      if (val === "" || val === undefined || val === null) {
        // RED error: field name specific
        let label = "";
        switch (field) {
          case "category_name": label = "Category Name"; break;
          // case "measuring_unit_id": label = "Measuring Unit"; break;
          // case "measuring_unit_value": label = "Measuring Value"; break;
          case "basic_door_price": label = "Basic Door Price"; break;
          case "double_door_price": label = "Double Door Price"; break;
          case "back_design_price": label = "Back Design Price"; break;
          case "back_puc_price": label = "Back PVC Price"; break;
          case "sagwan_door_price": label = "Sagwan Bidding Price"; break;
          default: label = field;
        }
        newErrors[field] = `${label} is required.`;
      } else if (numericFields.includes(field)) {
        if (!isNumeric(val)) {
          newErrors[field] = "Only positive numbers allowed.";
        } else if (Number(val) < 0) {
          newErrors[field] = "Value must be zero or greater.";
        }
      }
    });
    if (form.category_name.trim() === "") {
      newErrors.category_name = "Category Name is required.";
    } else if (form.category_name.length > 60) {
      newErrors.category_name = "Maximum 60 characters allowed.";
    }
    qualityList.forEach((q) => {
      const val = qualityPrices[String(q.id)];
      if (val === "" || val === undefined || val === null) {
        newErrors[`quality_${q.id}`] = `${q.name} price is required.`;
      } else if (!isNumeric(val) || Number(val) < 0) {
        newErrors[`quality_${q.id}`] = "Only zero or positive numbers allowed.";
      }
    });
    return newErrors;
  };

  // Auto-populate all fields except description/image from parent category
  const handleParentCategoryChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const parentId = e.target.value;
    setForm((prev: any) => ({ ...prev, parent_category_id: parentId }));
    if (!parentId) return;
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/product/categories?category_id=${encodeURIComponent(parentId)}`
      });
      const parentArr = res?.data;
      const parent = Array.isArray(parentArr) ? parentArr[0] : parentArr;
      if (parent) {
        setForm((prev: any) => ({
          ...prev,
          basic_door_price: parent.basic_door_price || "",
          double_door_price: parent.double_door_price || "",
          back_design_price: parent.back_design_price || "",
          back_puc_price: parent.back_puc_price || "",
          sagwan_door_price: parent.sagwan_door_price || "",
          hsn_code: parent.hsn_code || "",
        }));
        setQualityPrices(pricesFromCategory(parent));
      }
    } catch {
      toast.error("Failed to auto-populate parent category details");
    }
  };

  // Numeric-only handler (prevents alpha, negative, browser up/down nav)
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (
      value === "" ||
      (/^[0-9]+(\.[0-9]{0,2})?$/.test(value) && Number(value) >= 0)
    ) {
      setForm((prev: any) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Generic handler for string/select/textarea
  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev: any) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Image upload (optional)
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && allowedImageTypes.includes(file.type)) {
      setForm((prev: any) => ({ ...prev, image: file }));
      setImgPreview(URL.createObjectURL(file));
    } else {
      toast.error("Image must be JPG, PNG, or WEBP");
    }
  };
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && allowedImageTypes.includes(file.type)) {
      setForm((prev: any) => ({ ...prev, image: file }));
      setImgPreview(URL.createObjectURL(file));
    } else {
      toast.error("Image must be JPG, PNG, or WEBP");
    }
  };
  const removeImg = () => {
    setForm((prev: any) => ({ ...prev, image: null }));
    setImgPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Dropdown data load
  useEffect(() => {
    fetchDropdowns();
    if (editCategory) {
      setForm({
        ...initialForm,
        ...editCategory,
        hsn_code:
          editCategory.hsn_code &&
          String(editCategory.hsn_code).trim().toLowerCase() !== "null"
            ? String(editCategory.hsn_code)
            : "",
        image: null,
      });
      setQualityPrices(pricesFromCategory(editCategory));
      if (editCategory.category_img) setImgPreview(mediaUrl(editCategory.category_img));
    } else {
      setForm(initialForm);
      setQualityPrices({});
      setImgPreview(null);
    }
    // eslint-disable-next-line
  }, [editCategory]);

  const fetchDropdowns = async () => {
    try {
      const [unitRes, catRes, qualities] = await Promise.all([
        WebService.getAPI<any>({ action: "/v1/door-management/measuring-units" }),
        WebService.getAPI<any>({ action: "/v1/door-management/product/categories?type=NO_SUB_CATEGORY" }),
        fetchWoodQualities(),
      ]);
      setUnitList(
        (unitRes.data || []).map((u: any) => ({
          id: u.measuring_unit_id ?? u.id,
          name: u.measuring_unit ?? u.name,
        }))
      );
      setCatList(
        (catRes.data || []).map((c: any) => ({
          id: c.category_id ?? c.id,
          name: c.category_name ?? c.name,
        }))
      );
      setQualityList(qualities);
    } catch {
      toast.error("Dropdowns fetch failed");
    }
  };

  const handleAddWoodQuality = async () => {
    if (!inventoryAccess?.write) {
      toast.error("You do not have permission to add wood qualities.");
      return;
    }
    const entered = await promptAddWoodQuality();
    if (!entered) return;
    try {
      const created = await createWoodQuality({
        name: entered.name,
        category_prices: entered.category_prices,
      });
      setQualityList((prev) =>
        prev.some((q) => q.id === created.id)
          ? prev.map((q) => (q.id === created.id ? created : q))
          : [...prev, created]
      );
      const thisCategoryPrice = entered.category_prices.find(
        (p) => Number(p.category_id) === Number(form.category_id) || Number(p.category_id) === Number(editCategory?.category_id)
      );
      setQualityPrices((prev) => ({
        ...prev,
        [String(created.id)]: thisCategoryPrice != null ? String(thisCategoryPrice.price) : prev[String(created.id)] ?? "",
      }));
      toast.success("Wood quality added");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to add wood quality");
    }
  };

  const handleRenameWoodQuality = async (quality: DropdownOption) => {
    if (!inventoryAccess?.update && !inventoryAccess?.write) {
      toast.error("You do not have permission to update wood qualities.");
      return;
    }
    const name = await promptWoodQualityName("Rename wood quality", quality.name);
    if (!name || name === quality.name) return;
    try {
      const updated = await updateWoodQuality(quality.id, name);
      setQualityList((prev) => prev.map((q) => (q.id === quality.id ? updated : q)));
      toast.success("Wood quality updated");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update wood quality");
    }
  };

  const handleDeleteWoodQuality = async (quality: DropdownOption) => {
    if (!inventoryAccess?.delete) {
      toast.error("You do not have permission to delete wood qualities.");
      return;
    }
    const confirm = await Swal.fire({
      title: "Delete wood quality",
      text: `Delete "${quality.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteWoodQuality(quality.id);
      setQualityList((prev) => prev.filter((q) => q.id !== quality.id));
      setQualityPrices((prev) => {
        const next = { ...prev };
        delete next[String(quality.id)];
        return next;
      });
      toast.success("Wood quality deleted");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete wood quality");
    }
  };

  // SUBMIT (all validation/errors handled in React)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inventoryAccess?.write) {
      toast.error("You do not have permission to add or update categories.");
      return;
    }
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    // Final type-safe payload
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === "category_id") return;
      if (k === "image") {
        if (v && v instanceof Blob) {
          fd.append("image", v);
        }
      } else if (v !== undefined && v !== null && v !== "") {
        fd.append(k, String(v));
      }
    });
    fd.append(
      "qualities",
      JSON.stringify(
        qualityList.map((q) => ({
          quality_id: Number(q.id),
          id: Number(q.id),
          price: Number(qualityPrices[String(q.id)] || 0),
        }))
      )
    );
    qualityList.forEach((q) => {
      fd.append(`quality_price_${q.id}`, String(qualityPrices[String(q.id)] ?? "0"));
    });

    let apiUrl = "/v1/door-management/product/categories";
    const isEdit = !!editCategory?.category_id;
    if (isEdit) {
      apiUrl += `?category_id=${encodeURIComponent(editCategory.category_id)}`;
    }

    setLoading(true);
    try {
      await WebService.postAPI<FormData>({
        action: apiUrl,
        body: fd,
        isFormData: true,
      });

      // If this was an update, ask whether to override all products
      if (isEdit) {
        // make sure button isn't disabled during the prompt
        setLoading(false);
        const result = await Swal.fire({
          // title: "Override prices for all products?",
          text: "This will apply the updated basic door price to every product under this category.",
          icon: "info",
          showCancelButton: true,
          confirmButtonColor: "#FFD400",
          cancelButtonColor: "#aaa",
          confirmButtonText: "Yes, override all",
          cancelButtonText: "No, keep products as-is",
          heightAuto: false,
          focusCancel: true,
        });

        if (result.isConfirmed) {
          try {
            await WebService.postAPI({
              action: `/v1/door-management/product/categories/${editCategory.category_id}/override-product-price`,
            });
            toast.success("Category updated and product prices overridden.");
          } catch {
            toast.error("Category updated, but failed to override product prices.");
          }
        } else {
          toast.success("Category updated.");
        }
      } else {
        toast.success("Category added");
      }

      onSuccess?.();
      onClose();
    } catch {
      toast.error(isEdit ? "Failed to update category" : "Failed to add category");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.panel}>
        {/* X Close Button */}
        <CrossButton onClick={onClose} />

        <h2 className={styles.heading}>
          {editCategory ? "Edit Category" : "Add New Category"}
        </h2>

        <form className={styles.panelBody} onSubmit={handleSubmit} autoComplete="off">
          {/* Basic Info */}
          <div className={styles.sectionTitle}>
            <FaInfoCircle color="#0d6efd" style={{ marginRight: 6 }} /> Basic Information
          </div>

          <div className={styles.sectionGroup2col}>
            <div>
              <label className={styles.label}>Category Name *</label>
              <input
                name="category_name"
                value={form.category_name}
                onChange={handleChange}
                className={`${styles.input} ${errors.category_name ? styles.inputError : ""}`}
                maxLength={60}
                autoComplete="off"
                placeholder="Category Name"
              />
              {errors.category_name && <div className={styles.error}>{errors.category_name}</div>}
            </div>

            <div>
              <label className={styles.label}>Parent Category</label>
              <select
                name="parent_category_id"
                value={form.parent_category_id || ""}
                onChange={handleParentCategoryChange}
                className={styles.input}
              >
                <option value="">None</option>
                {catList
                  .filter((c) => !editCategory || c.id !== editCategory.category_id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* HSN (optional) */}
            <div>
              <label className={styles.label}>
                HSN Code <span style={{ color: "#9aa0a6", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                name="hsn_code"
                value={form.hsn_code || ""}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g., 4414 / 44.14 / ABC-123"
                maxLength={30}
                autoComplete="off"
              />
              {errors.hsn_code && <div className={styles.error}>{errors.hsn_code}</div>}
            </div>
          </div>

          {/* Basic Door Price */}
          <div className={styles.sectionTitle}>
            <FaRupeeSign color="#12b76a" style={{ marginRight: 6 }} /> Basic Door Price
          </div>
          <div className={styles.sectionSubtitle}>
            Single Door / Back Door / Bubble Packaging *
          </div>
          <div>
            <input
              name="basic_door_price"
              value={form.basic_door_price}
              onChange={handleNumericChange}
              className={`${styles.input} ${errors.basic_door_price ? styles.inputError : ""}`}
              type="number"
              min="0"
              step="any"
              placeholder="Basic Door Price"
              autoComplete="off"
            />
            {errors.basic_door_price && <div className={styles.error}>{errors.basic_door_price}</div>}
          </div>

          {/* Additional Pricing Set — Door Configuration */}
          <div className={styles.sectionTitle}>Door Configuration Prices</div>
          <div className={styles.sectionGroup2col}>
            {[
              { label: "Double Door Price", name: "double_door_price" },
              { label: "Back Design Price", name: "back_design_price" },
              { label: "Back PVC Price", name: "back_puc_price" },
              { label: "Sagwan Bidding Price", name: "sagwan_door_price" },
            ].map((item) => (
              <div key={item.name}>
                <label className={styles.label}>{item.label} *</label>
                <input
                  name={item.name}
                  value={form[item.name]}
                  onChange={handleNumericChange}
                  className={`${styles.input} ${errors[item.name] ? styles.inputError : ""}`}
                  placeholder="Price including Tax."
                  type="number"
                  min="0"
                  step="any"
                  autoComplete="off"
                />
                {errors[item.name] && <div className={styles.error}>{errors[item.name]}</div>}
              </div>
            ))}
          </div>

          <div className={styles.qualityHead}>
            <div>
              <div className={styles.sectionTitle} style={{ marginTop: 0 }}>Wood Quality Prices</div>
              <div className={styles.sectionSubtitle}>
                Pre-defined price per product category. Add, rename, or delete wood qualities here.
              </div>
            </div>
            {inventoryAccess?.write && (
              <button type="button" className={styles.addQualityBtn} onClick={handleAddWoodQuality}>
                <MdAdd style={{ marginRight: 4 }} /> Add wood quality
              </button>
            )}
          </div>
          <div className={styles.sectionGroup2col}>
            {qualityList.length === 0 ? (
              <div className={styles.muted}>
                No wood qualities found. Add one to set a price for this category.
              </div>
            ) : (
              qualityList.map((q) => (
                <div key={q.id}>
                  <div className={styles.qualityLabelRow}>
                    <label className={styles.label}>{q.name} *</label>
                    <span className={styles.qualityActions}>
                      {(inventoryAccess?.update || inventoryAccess?.write) && (
                        <FaEdit
                          title="Rename"
                          onClick={() => handleRenameWoodQuality(q)}
                        />
                      )}
                      {inventoryAccess?.delete && (
                        <FaTrash
                          title="Delete"
                          onClick={() => handleDeleteWoodQuality(q)}
                        />
                      )}
                    </span>
                  </div>
                  <input
                    name={`quality_${q.id}`}
                    value={qualityPrices[String(q.id)] ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        (/^[0-9]+(\.[0-9]{0,2})?$/.test(value) && Number(value) >= 0)
                      ) {
                        setQualityPrices((prev) => ({ ...prev, [String(q.id)]: value }));
                        setErrors((prev) => ({ ...prev, [`quality_${q.id}`]: "" }));
                      }
                    }}
                    className={`${styles.input} ${errors[`quality_${q.id}`] ? styles.inputError : ""}`}
                    placeholder="Price including Tax."
                    type="number"
                    min="0"
                    step="any"
                    autoComplete="off"
                  />
                  {errors[`quality_${q.id}`] && (
                    <div className={styles.error}>{errors[`quality_${q.id}`]}</div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Description */}
          <div className={styles.sectionTitle}>
            <FaFileAlt color="#16a085" style={{ marginRight: 6 }} /> Description
          </div>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            className={styles.input}
            rows={3}
            placeholder="Add relevant notes or description…"
            style={{ marginBottom: 14, minHeight: 60 }}
            maxLength={1000}
            autoComplete="off"
          />

          {/* Category Image */}
          <div className={styles.sectionTitle}>
            <FaImage color="#e03985" style={{ marginRight: 6 }} /> Category Image
          </div>
          <div
            className={styles.dragDrop}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            {imgPreview ? (
              <div style={{ position: "relative", width: 90, margin: "auto" }}>
                <img
                  src={imgPreview}
                  alt="Preview"
                  style={{ width: 90, borderRadius: 8, marginBottom: 5 }}
                />
                <button
                  type="button"
                  title="Remove"
                  onClick={removeImg}
                  className={styles.removeImgBtn}
                >
                  ×
                </button>
              </div>
            ) : (
              <>
                <div>
                  <FaImage size={36} color="#bbb" />
                </div>
                <div style={{ color: "#888", margin: "8px 0 2px 0" }}>
                  Drag and drop your image here, or click to browse
                </div>
                <label
                  htmlFor="image-upload"
                  style={{ fontSize: 13, color: "#aaa" }}
                >
                  Supported formats: JPG, PNG, WEBP
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="image-upload"
                  style={{ display: "none" }}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFile}
                />
              </>
            )}
          </div>

          {/* Buttons */}
          <div className={styles.buttonRow}>
            <button
              type="submit"
              className={styles.addBtn}
              disabled={loading || !inventoryAccess?.write}
              style={{
                minWidth: 180,
                marginRight: 10,
                opacity: !inventoryAccess?.write ? 0.5 : 1,
                cursor: !inventoryAccess?.write ? "not-allowed" : undefined,
              }}
            >
              {loading
                ? "Saving..."
                : editCategory
                  ? "Update Category"
                  : "Add Category"}
            </button>
            <button
              type="button"
              className={styles.closeBtn}
              disabled={loading}
              onClick={onClose}
              style={{ minWidth: 120 }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCategoriesPanel;
